import CheckIcon from "@mui/icons-material/Check";
import { Box, ButtonBase, ListItem, Typography } from "@mui/material";
import type { AnalysisVersionInfo } from "Infrastructure/ApiZfp/Schema";
import { useDateFnsLocale } from "Infrastructure/Hooks/DateFns/useDateFnsLocale";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { format } from "date-fns";
import type { Ref } from "react";
import { AnalysisStatusChip } from "../_Shared/AnalysisStatusChip";

const ROW_MIN_HEIGHT = 52;
const CHECK_SLOT_WIDTH = 16;

type Props = {
  readonly version: AnalysisVersionInfo;
  readonly isDisplayed: boolean;
  readonly buttonRef?: Ref<HTMLButtonElement>;
  readonly onOpen: (versionId: string) => void;
};

export const VersionSelectItem = ({ version, isDisplayed, buttonRef, onOpen }: Props) => {
  const { t } = useResource();
  const locale = useDateFnsLocale();

  return (
    <ListItem
      disablePadding
      sx={{
        borderRadius: Radius.M,
        bgcolor: isDisplayed ? "primary.light" : "transparent",
        "&:hover": { bgcolor: isDisplayed ? "primary.light" : "grey.50" },
      }}
    >
      <ButtonBase
        ref={buttonRef}
        aria-current={isDisplayed || undefined}
        onClick={() => onOpen(version.versionId)}
        sx={{
          width: "100%",
          minHeight: ROW_MIN_HEIGHT,
          py: 1.5,
          pl: Spacing.XL,
          pr: Spacing.M,
          gap: Spacing.L,
          justifyContent: "flex-start",
          textAlign: "left",
          borderRadius: Radius.M,
          "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 },
        }}
      >
        <Box component="span" display="flex" width={CHECK_SLOT_WIDTH} flexShrink={0} color="primary.main">
          {isDisplayed && <CheckIcon sx={{ fontSize: CHECK_SLOT_WIDTH }} />}
        </Box>
        <Box component="span" flex={1} minWidth={0}>
          <Typography component="span" display="block" noWrap fontSize={14} fontWeight={500} color="text.primary">
            {t(Resources.AnalysisDetail.VersionSelect.Version, { version: version.version })}
          </Typography>
          <Typography component="span" display="block" noWrap fontSize={12} color="text.secondary">
            {format(new Date(version.createdAt), "d. M. yyyy", { locale })}
          </Typography>
        </Box>
        <AnalysisStatusChip variant="filled" size="small" fontWeight={500} state={version.state} />
      </ButtonBase>
    </ListItem>
  );
};
